import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import { IGNORE_REASON_LABEL } from '@masamune/shared';
import { incassiQuery, daysFromToday, plurale, METHOD_LABEL, type IncassiFilter, type IncassoRow } from './adminApi';
import { useAdminActions } from './useAdminActions';
import { SettlePopover } from './SettlePopover';
import { TrashPopover } from './TrashPopover';

/**
 * Incassi — le rate delle fatture emesse (FiC ha rate multiple: una riga per rata).
 *   filterbar → pill Aperte / Incassate / Tutte / Cestinate · cerca · contatore
 *   tabella   → Fattura/Cliente · Rata n/N · Scadenza · Importo · Stato · Metodo · Incassa
 * Stato in URL: status · q (es. /admin?tab=incassi&status=incassate)
 *
 * Cestinate è il cestino: le fatture tolte dal gestionale (scartate dallo SdI,
 * doppioni, errori) con motivo e data e il tasto Ripristina. Una riga per
 * fattura, non per rata. Cestinare NON annulla niente su FattureInCloud.
 *
 * La selezione multipla serve al caso vero per cui è nata: cinque fatture
 * scartate dallo SdI si tolgono in un colpo, non una alla volta.
 */
const FILTERS: Array<{ key: IncassiFilter; label: string }> = [
  { key: 'aperte', label: 'Aperte' },
  { key: 'incassate', label: 'Incassate' },
  { key: 'tutte', label: 'Tutte' },
  { key: 'cestinate', label: 'Cestinate' },
];
const PAGE_SIZE = 25;

export function IncassiTab({ canWrite, showHistory }: { canWrite: boolean; showHistory?: boolean }) {
  const openModal = useModalStore((s) => s.open);
  const [statusParam, setStatus] = useUrlState<string>('status', 'aperte');
  const status: IncassiFilter = FILTERS.some((f) => f.key === statusParam) ? (statusParam as IncassiFilter) : 'aperte';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  // Selezione per FATTURA (cestinare toglie il documento, non la singola rata)
  const [selected, setSelected] = useState<string[]>([]);
  const [trashOpen, setTrashOpen] = useState(false);
  const { ignoreDocs } = useAdminActions();
  useEffect(() => { setPage(1); }, [status, search, showHistory]);
  useEffect(() => { setSelected([]); setTrashOpen(false); }, [status, search, showHistory, page]);

  const params = useMemo(
    () => ({ status, search: search.trim() || undefined, includeHistory: showHistory, page, pageSize: PAGE_SIZE }),
    [status, search, showHistory, page],
  );
  const q = useQuery(incassiQuery(params));
  const items = q.data?.items ?? [];
  const trash = status === 'cestinate';
  // La colonna Rata ha senso solo se c'è almeno una fattura a più rate (o l'API dice quante sono)
  const showRata = !trash && items.some((r) => r.ofTotal > 1 || r.seq > 1);
  // La selezione multipla è il lavoro lungo di questa schermata (cinque fatture
  // scartate dallo SdI si tolgono in un colpo) e sul telefono non si fa: resta
  // al computer (mobile.md §0). Toglierla toglie anche la casella da 13×13,
  // l'unico bersaglio dell'Amministrazione sotto i 44px richiesti.
  const mobile = useIsMobile();
  const pickable = canWrite && !trash && !mobile;
  const invoiceIds = useMemo(() => [...new Set(items.map((r) => r.invoiceId))], [items]);
  const allPicked = invoiceIds.length > 0 && invoiceIds.every((id) => selected.includes(id));
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <>
      {/* Quattro pill, la ricerca e i bottoni facevano 160px impilati su 390:
          dietro un tasto solo (mobile.md §5). Sul computer non cambia niente. */}
      <MobileFilters attivi={[search.trim(), status !== 'aperte' ? status : ''].filter(Boolean).length}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="flex items-center gap-1.5" role="group" aria-label="Stato rate">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatus(f.key)} aria-pressed={status === f.key} className={status === f.key ? 'filter-pill is-active' : 'filter-pill'}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="divider" />
        <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca rate" placeholder="Cerca fattura o cliente…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <span className="mute text-[12px]">
          {trash ? plurale(q.data?.total ?? 0, 'cestinata', 'cestinate') : plurale(q.data?.total ?? 0, 'rata', 'rate')}
        </span>
        {/* Azione di massa: compare solo quando c'è qualcosa di selezionato */}
        {pickable && selected.length > 0 && (
          <div className="relative">
            <button className="btn btn-sm" disabled={ignoreDocs.isPending} onClick={() => setTrashOpen((v) => !v)}>
              <Icon name="trash" size={10} /> Cestina {selected.length} selezionate
            </button>
            {trashOpen && (
              <TrashPopover
                title={`${selected.length} fatture`}
                pending={ignoreDocs.isPending}
                onClose={() => setTrashOpen(false)}
                onConfirm={(v) => {
                  setTrashOpen(false);
                  const ids = selected;
                  setSelected([]);
                  ignoreDocs.mutate({ kind: 'invoice', ids, label: `${ids.length} fatture`, ...v });
                }}
              />
            )}
          </div>
        )}
        {/* Non è un filtro: sul telefono va in fondo, dove arriva il pollice. */}
        {canWrite && !trash && (
          <button className="btn btn-sm d-only" onClick={() => openModal('newCashEntry', { direction: 'IN' })}>
            <Icon name="banknote" size={10} /> Nuovo incasso in contanti
          </button>
        )}
      </div>
      </MobileFilters>

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={6} cols={6} withAvatar={false} /></tbody></table></div>
      ) : items.length === 0 ? (
        search.trim() ? (
          <EmptyState brand title="Nessuna rata con questa ricerca" description="Prova con il numero della fattura o il nome del cliente."
            action={<button className="btn" onClick={() => setSearchParams((p) => { const sp = new URLSearchParams(p); sp.delete('q'); return sp; })}>Azzera la ricerca</button>} />
        ) : trash ? (
          <EmptyState brand title="Cestino vuoto" description="Qui finiscono le fatture tolte dal gestionale: quelle scartate dallo SdI, i doppioni, gli errori. Si possono sempre ripristinare." />
        ) : status === 'aperte' ? (
          <EmptyState brand title="Niente da incassare" description="Tutte le rate emesse risultano incassate." />
        ) : (
          <EmptyState brand title="Nessuna rata" description="Le rate delle fatture emesse compaiono qui dopo la sincronizzazione con FattureInCloud." />
        )
      ) : (
        <div className="ed-section">
          {trash ? (
            <table className="tbl tbl--ed tbl--card tbl--static">
              <thead>
                <tr>
                  <th>Fattura</th>
                  <th style={{ width: 130 }}>Cestinata il</th>
                  <th>Motivo</th>
                  <th className="num" style={{ width: 130 }}>Importo</th>
                  <th style={{ width: 130 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((r) => <TrashRow key={r.id} r={r} canWrite={canWrite} />)}
              </tbody>
            </table>
          ) : (
            /* Scheda sul telefono. Niente chevron (`tbl--static`): la riga non
               apre un dettaglio, il gesto è "Incassa" dentro la scheda. */
            <table className="tbl tbl--ed tbl--card tbl--static">
              <thead>
                <tr>
                  {pickable && (
                    <th style={{ width: 34 }}>
                      <input type="checkbox" aria-label="Seleziona tutte" checked={allPicked}
                        onChange={() => setSelected(allPicked ? [] : invoiceIds)} />
                    </th>
                  )}
                  <th>Fattura</th>
                  {showRata && <th style={{ width: 90 }}>Rata</th>}
                  <th style={{ width: 170 }}>Scadenza</th>
                  <th className="num" style={{ width: 130 }}>Importo</th>
                  <th style={{ width: 130 }}>Stato</th>
                  <th style={{ width: 110 }}>Metodo</th>
                  <th style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <Row key={r.id} r={r} canWrite={canWrite} showRata={showRata}
                    pickable={pickable} picked={selected.includes(r.invoiceId)} onPick={() => toggle(r.invoiceId)} />
                ))}
              </tbody>
            </table>
          )}
          {q.data && <Pagination page={page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}

      {canWrite && !trash && (
        <DesktopOnly
          cosa="La registrazione di un nuovo incasso in contanti"
          perche="richiede la compilazione contabile completa; dal telefono resta il gesto rapido Incassa sulle fatture aperte."
        >
          <div className="m-actionbar m-only">
            <button className="btn btn-primary" onClick={() => openModal('newCashEntry', { direction: 'IN' })}>
              <Icon name="banknote" size={12} /> Nuovo incasso in contanti
            </button>
          </div>
        </DesktopOnly>
      )}
    </>
  );
}

function Row({ r, canWrite, showRata, pickable, picked, onPick }: {
  r: IncassoRow; canWrite: boolean; showRata: boolean; pickable: boolean; picked: boolean; onPick: () => void;
}) {
  const { markPaid, unmarkPaid, ignoreDocs } = useAdminActions();
  const [open, setOpen] = useState(false);
  const [trash, setTrash] = useState(false);
  const paid = r.status === 'INCASSATA';
  const reversed = r.status === 'STORNATA';
  const days = daysFromToday(r.dueDate);
  const overdue = !paid && !reversed && days != null && days < 0;
  const label = `${r.invoiceNumber} · ${r.client.shortName}`;
  const dueLabel = paid || reversed ? fmtDate(r.dueDate)
    : days == null ? '—'
    : days < 0 ? `in ritardo di ${-days} g`
    : days === 0 ? 'oggi'
    : days === 1 ? 'domani'
    : days <= 6 ? `tra ${days} g`
    : fmtDate(r.dueDate);
  return (
    <tr style={{ cursor: 'default' }} className={picked ? 'bg-paper-2' : undefined}>
      {pickable && (
        <td onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label={`Seleziona ${label}`} checked={picked} onChange={onPick} />
        </td>
      )}
      <td data-m="hd">
        <div className="mono font-semibold">{r.invoiceNumber}</div>
        <div className="text-[12px] mute">{r.client.shortName}</div>
      </td>
      {/* ofTotal = 0 quando l'API non dice quante rate ha la fattura: si mostra solo il numero.
          In scheda la cella sparisce quando non ha niente da dire: una chiave
          che vale "—" occupa metà riga per non dire nulla (mobile.md §3). */}
      {showRata && (
        <td data-m={r.ofTotal > 1 || r.seq > 1 ? 'key' : 'off'} data-l="Rata" className="mono mute">
          {r.ofTotal > 1 ? `${r.seq}/${r.ofTotal}` : r.ofTotal === 1 ? 'unica' : r.seq > 1 ? `n. ${r.seq}` : '—'}
        </td>
      )}
      <td data-m="key" data-l="Scadenza" className="mono text-[13px]" style={{ whiteSpace: 'nowrap' }}>
        <span className={overdue ? 'text-alert font-semibold' : days != null && days <= 1 && !paid ? 'text-warn' : 'mute'}>{dueLabel}</span>
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold">€ {fmtEur(r.amount, true)}</td>
      <td data-m="key" data-l="Stato">
        {paid
          ? <Chip tone="ok" dot>Incassata{r.paidDate ? ` · ${fmtDate(r.paidDate)}` : ''}</Chip>
          : reversed ? <Chip tone="ghost" dot>Stornata</Chip>
          : overdue ? <Chip tone="alert" dot>Scaduta</Chip> : <Chip tone="warn" dot>Da incassare</Chip>}
      </td>
      {/* Il metodo sta sempre a "—" finché la rata è aperta, ed è proprio lo
          stato in cui si guarda questa lista dal telefono: si legge aprendo il
          dettaglio (mobile.md §3, "un dato che sta sempre a — è off"). */}
      <td data-m="off" className="mute text-[13px]">{r.method ? METHOD_LABEL[r.method] ?? r.method : '—'}</td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end items-center gap-1 relative">
          {canWrite && !reversed && (paid ? (
            <button className="btn btn-sm btn-ghost" disabled={unmarkPaid.isPending} title="Riporta a da incassare" onClick={() => unmarkPaid.mutate({ paymentId: r.id, label })}>
              <Icon name="undo" size={10} /> Riapri
            </button>
          ) : (
            <button className="btn btn-sm" disabled={markPaid.isPending} title="Registra l'incasso" onClick={() => setOpen((v) => !v)}>
              <Icon name="check" size={10} /> Incassa
            </button>
          ))}
          {canWrite && (
            <ActionsMenu
              items={[{ key: 'trash', label: 'Cestina', icon: 'trash', danger: true, onClick: () => setTrash(true) }]}
            />
          )}
          {open && (
            <SettlePopover
              mode="incassa"
              amount={r.amount}
              target={{ kind: 'INVOICE_PAYMENT', id: r.id }}
              // La fattura sta anche su FiC: se la chiudiamo qui vale la pena dire perché.
              withNote
              pending={markPaid.isPending}
              onClose={() => setOpen(false)}
              onConfirm={(v) => {
                setOpen(false);
                markPaid.mutate({ paymentId: r.id, amount: r.amount, label, ...v });
              }}
            />
          )}
          {trash && (
            <TrashPopover
              title={r.invoiceNumber}
              pending={ignoreDocs.isPending}
              onClose={() => setTrash(false)}
              onConfirm={(v) => { setTrash(false); ignoreDocs.mutate({ kind: 'invoice', ids: [r.invoiceId], label, ...v }); }}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

/** Riga del cestino: perché è sparita, quando, e come farla tornare. */
function TrashRow({ r, canWrite }: { r: IncassoRow; canWrite: boolean }) {
  const { restoreDocs } = useAdminActions();
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd">
        <div className="mono font-semibold">{r.invoiceNumber}</div>
        <div className="text-[12px] mute">{r.client.shortName}</div>
      </td>
      <td data-m="key" data-l="Cestinata il" className="mono mute text-[13px]">{fmtDate(r.ignoredAt)}</td>
      <td data-m="row" data-l="Motivo">
        <div className="text-[13px]">{r.ignoredReason ? IGNORE_REASON_LABEL[r.ignoredReason] ?? r.ignoredReason : '—'}</div>
        {r.ignoredNote && <div className="text-[12px] mute">{r.ignoredNote}</div>}
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold mute">€ {fmtEur(r.amount, true)}</td>
      <td data-m="row">
        <div className="flex justify-end">
          {canWrite && (
            <button className="btn btn-sm btn-ghost" disabled={restoreDocs.isPending}
              title="Rimettila fra le fatture" onClick={() => restoreDocs.mutate({ kind: 'invoice', ids: [r.invoiceId], label: r.invoiceNumber })}>
              <Icon name="undo" size={10} /> Ripristina
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
